import { z } from 'zod';
import { optionalString, requiredString } from './common';
import { signatureDataUrlSchema } from './signature';

/**
 * The reporting doctor a released report is signed by.
 *
 * `qualifications` and `designation` are required, unlike on a staff record:
 * a signature block with a bare name on a clinical document is not one a
 * consultant would put their name to.
 */
export const createConsultantSchema = z.object({
  name: requiredString('Name'),
  qualifications: requiredString('Qualifications'),
  designation: requiredString('Designation'),
  registration_no: optionalString(),
  signature_data_url: signatureDataUrlSchema,
  /**
   * Retiring a consultant, rather than deleting them.
   *
   * A consultant who has signed a report can never be deleted — the report
   * relation is `Restrict` — so this is how they leave the picker. Defaults to
   * true, because the only reason to add one is to start using them.
   */
  is_active: z.boolean().default(true),
});
export type CreateConsultantInput = z.infer<typeof createConsultantSchema>;

export const updateConsultantSchema = createConsultantSchema;
export type UpdateConsultantInput = z.infer<typeof updateConsultantSchema>;

/** `GET /consultants?active=true` — the picker only wants the ones still in use. */
export const consultantQuerySchema = z.object({
  search: z.string().trim().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  active: z.enum(['true', 'false']).optional(),
});
export type ConsultantQuery = z.infer<typeof consultantQuerySchema>;
